<template>
    <v-dialog v-model="dialog" width="1200" :fullscreen="isMobileOrTablet()" persistent>
        <v-card class="rounded-lg" style="display: flex; flex-direction: column; height: 100%;">
            <v-card-title style="background-color: #1a5b0f !important; display: flex; justify-content: space-between; align-items: center;">
                <div class="w-75 text-truncate text-white">
                    <span class="font-weight-bold text-h6">{{ dialog_title }}</span>
                    <div class="text-caption">{{ dialog_sub_title }}</div>
                </div>
                <v-btn v-if="!loading" flat icon color="darkGreen" @click="closeDialog()">
                    <v-icon color="white">mdi-close</v-icon>
                </v-btn>
            </v-card-title>

            <v-divider></v-divider>

            <v-card-text style="overflow-y: auto; max-height: calc(100vh - 160px);">
                <v-row dense>
                    <v-col cols="12">
                        <v-card variant="outlined" rounded="lg" class="pa-4 bg-grey-lighten-5">
                            <v-row dense>
                                <v-col cols="12" md="6">
                                    <div class="text-caption text-grey-darken-1">Course</div>
                                    <div class="font-weight-bold">{{ faculty_load?.code }} - {{ faculty_load?.title }}</div>
                                </v-col>
                                <v-col cols="12" md="3">
                                    <div class="text-caption text-grey-darken-1">Section</div>
                                    <div class="font-weight-bold">{{ faculty_load?.section_code || 'N/A' }}</div>
                                </v-col>
                                <v-col cols="12" md="3">
                                    <div class="text-caption text-grey-darken-1">Semester</div>
                                    <div class="font-weight-bold">
                                        {{ $store.state.semester_schoolyear?.semester }} {{ $store.state.semester_schoolyear?.schoolyear }}
                                    </div>
                                </v-col>
                                <!-- <v-col cols="12" md="3">
                                    <div class="text-caption text-grey-darken-1">Faculty</div>
                                    <div class="font-weight-bold">{{ faculty_load?.faculty_name || 'N/A' }}</div>
                                </v-col> -->
                                <v-col cols="12" md="4">
                                    <div class="text-caption text-grey-darken-1">Entered Scores</div>
                                    <v-chip color="green" size="small" variant="elevated">
                                        {{ enteredScoresCount }} / {{ course_outcomes.length }}
                                    </v-chip>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <div class="text-caption text-grey-darken-1">Average Score</div>
                                    <div class="font-weight-bold">{{ averageScore !== null ? averageScore : 'N/A' }}</div>
                                </v-col>
                                
                            </v-row>
                        </v-card>
                    </v-col>

                    <v-col cols="12" v-if="loading">
                        <v-progress-linear indeterminate color="primary" height="5" class="mt-1 rounded-lg"></v-progress-linear>
                    </v-col>

                    <v-col cols="12" v-else-if="course_outcomes.length === 0">
                        <v-alert type="warning" variant="tonal" rounded="lg">
                            No course outcomes are set up yet for this course load. Scores cannot be entered until the course outcomes exist.
                        </v-alert>
                    </v-col>

                    <v-col cols="12" v-for="item in course_outcomes" :key="item.id">
                        <v-card variant="outlined" rounded="lg" class="pa-4">
                            <v-row dense>
                                <v-col cols="12" md="2">
                                    <div class="text-caption text-grey-darken-1">CO Code</div>
                                    <div class="font-weight-bold">{{ item.code }}</div>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <div class="text-caption text-grey-darken-1">Description</div>
                                    <div>{{ item.description }}</div>
                                </v-col>
                                <v-col cols="12" md="2">
                                    <div class="text-caption text-grey-darken-1">Assessment Tool</div>
                                    <div>{{ item.assessment_tools }}</div>
                                </v-col>
                                <v-col cols="12" md="2">
                                    <div class="text-caption text-grey-darken-1">Time Frame</div>
                                    <div>{{ item.time_frame }}</div>
                                </v-col>
                                <v-col cols="12" md="2">
                                    <v-text-field
                                        v-model="item.score"
                                        label="Score (%)"
                                        variant="outlined"
                                        rounded="lg"
                                        type="number"
                                        min="0"
                                        max="100"
                                        step="0.01"
                                        color="darkGreen"
                                        hide-details
                                        @update:model-value="onScoreInput(item, $event)"
                                    ></v-text-field>
                                </v-col>
                            </v-row>
                        </v-card>
                    </v-col>
                </v-row>
            </v-card-text>

            <v-divider></v-divider>

            <v-card-actions class="pa-3" style="margin-top: auto;">
                <v-spacer></v-spacer>
                <v-btn class="mr-1" :disabled="loading" text="Cancel" variant="outlined" elevation="1" @click="closeDialog()"></v-btn>
                <v-btn
                    class="mr-1"
                    :loading="loading"
                    :disabled="course_outcomes.length === 0"
                    text="Save Scores"
                    variant="elevated"
                    elevation="1"
                    color="darkGreen"
                    @click="saveScores()"
                ></v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { defineComponent } from 'vue'
import { useSnackbar } from '@/composables/useSnackbar'

export default defineComponent({
    emits: ['refresh'],
    data() {
        return {
            dialog: false,
            dialog_title: 'Course Outcome Scores',
            dialog_sub_title: null,
            loading: false,
            faculty_load: null,
            course_outcomes: [],
        }
    },
    computed: {
        enteredScoresCount() {
            return this.course_outcomes.filter((item) => item.score !== null && item.score !== '').length
        },
        averageScore() {
            const numericScores = this.course_outcomes
                .map((item) => item.score)
                .filter((score) => score !== null && score !== '' && !Number.isNaN(Number(score)))
                .map((score) => Number(score))

            if (numericScores.length === 0) {
                return null
            }

            return Number((numericScores.reduce((total, score) => total + score, 0) / numericScores.length).toFixed(2))
        },
    },
    methods: {
        async init(facultyLoad) {
            this.dialog = true
            this.loading = true
            this.faculty_load = null
            this.course_outcomes = []
            this.dialog_sub_title = facultyLoad.code + ' - ' + facultyLoad.title + ' (' + (facultyLoad.section_code || 'No Section') + ')'

            const res = await this.axiosCall(
                '/course-outcomes-scores/faculty-load/' + facultyLoad.id,
                'get',
                null,
                true,
            )

            if (res && res.status === 200) {
                this.faculty_load = res.data.faculty_load
                this.course_outcomes = res.data.course_outcomes.map((item) => ({
                    ...item,
                    score: item.score,
                }))
            }

            this.loading = false
        },
        onScoreInput(item, value) {
            if (value === '' || value === null || value === undefined) {
                item.score = null
                return
            }

            item.score = value
        },
        async saveScores() {
            const { showSnackbar } = useSnackbar()

            const scores = []
            for (const item of this.course_outcomes) {
                if (item.score === null || item.score === '') {
                    scores.push({
                        course_outcome_id: item.id,
                        score: null,
                    })
                    continue
                }

                const parsedScore = Number(item.score)
                if (Number.isNaN(parsedScore) || parsedScore < 0 || parsedScore > 100) {
                    showSnackbar('Each score must be a number between 0 and 100.', 'error')
                    return
                }

                scores.push({
                    course_outcome_id: item.id,
                    score: parsedScore,
                })
            }

            this.loading = true

            const res = await this.axiosCall(
                '/course-outcomes-scores/save',
                'post',
                {
                    faculty_load_id: this.faculty_load.id,
                    scores,
                },
                true,
            )

            if (res && res.data && res.data.status === 200) {
                this.loading = false
                this.$emit('refresh')
                showSnackbar('Course outcome scores successfully saved!')
                this.closeDialog()
                return
            }

            this.loading = false
            showSnackbar(res?.data?.message || 'Failed to save course outcome scores.', 'error')
        },
        closeDialog() {
            this.dialog = false
            this.faculty_load = null
            this.course_outcomes = []
        },
    },
})
</script>

<style scoped>
</style>